<template>
  <v-dialog
      fullscreen
      eager
      scrollable
      v-model="dialog"
      hide-overlay
      content-class="form-dialog"
  >
    <LoadingDialog ref="loading"
                   :loader-msg="!isSupportDialog ?
                   $t('book/facility:facility.dashboard.information.state.generate-wait-gbu-report') :
                   $t('book/facility:facility.dashboard.information.state.generate-wait-gbu-support-report')"/>

    <v-card elevation="0">
      <div class="text-right px-4 mt-4">
        <v-btn icon @click="cancelDialog" ><v-icon large color="primary">close</v-icon></v-btn>
      </div>
      <v-card-text v-if="isSupportDialog">
        <v-form ref="supportForm">
          <h3 class="font-weight-bold px-sm-5 mb-0">Erstelle Unterstützungsbericht für die Gefahrenüberprüfung</h3>
          <div>
            <v-textarea
                v-model="allProtections.gbuClient"
                label="GBU Auftraggeber"
                color="primary"
                ref="textAreaGbuClient"
                outlined
                class="mt-4"
                validate-on-blur
                :rules="[ruleRequired($t('utils:rules.required'))]"
            />
          </div>
          <BaseButton
              color="primary"
              btnText="Erzeuge Unterstützungs-Bericht"
              @clickedButton="generateSupportReport()"
          />
          <BaseButton
              :btn-text="$t('buttons:cancel')"
              color="grey"
              @clickedButton="cancelDialog"
          />
        </v-form>
      </v-card-text>
      <v-card-text v-else>
        <div ref="top"></div>
        <v-form ref="form">
          <h3 class="font-weight-bold px-sm-5 mb-0">Erstelle Befund zur Gefahrenüberprüfung</h3>
          <div v-if="!data">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <div v-else>
            <v-textarea
                v-model="gbuClient"
                label="GBU Auftraggeber"
                color="primary"
                ref="textAreaGbuClient"
                outlined
                class="mt-4"
                validate-on-blur
                :rules="[ruleRequired($t('utils:rules.required'))]"
            />
            <v-checkbox color="primary" v-model="useCostEstimate"
                        label="Kostenabschätzung hinzufügen" />

            <v-radio-group v-model="changeRisk" label="Risikostufe änderbar für:">
              <v-radio label="nur änderbar gekennzeichnete Risikostufen" value="selected"></v-radio>
              <v-radio label="alle Risikostufen" value="all"></v-radio>
            </v-radio-group>
            <div v-if="data.length === 0" >
              <p class="mt-2 mb-6 font-weight-bold">Es wurden keinerlei Gefahren bei der Gefahrenüberprüfung festgestellt.</p>
            </div>
            <div v-for="chapter in data" :key="chapter.nr+chapter.title">
              <h4 class="primary--text font-weight-medium mt-3">{{chapter.nr + ' ' +chapter.title }}</h4>
              <div v-for="check in chapter.checks" :key="check.nr">
                <h5>{{check.nr}} {{check.check}}</h5>
                <p v-if="check.sect">EN 81-80: {{check.sect}}</p>
                <div v-if="check.risk !== 'info'">
                  <p v-if="check.note" class="text-pre-wrap">Bemerkung vom SV: {{check.note}}</p>
                  <v-row>
                    <v-col>
                      <p>Default Risikostufe: {{check.risk}}</p>
                      <p v-if="riskRemark(check.risk)">{{riskRemark(check.risk)}}</p>
                    </v-col>
                      <v-col>
                      <div v-if="canChangeRisk(check)"
                           class="pt-2"
                           :style="'max-width:300px; background-color: '+getRiskColor(check.uiRisk)">
                        <v-select
                            v-model="check.uiRisk"
                            label="Risikostufe"
                            :items="['hoch', 'mittel', 'niedrig']"
                            outlined
                            hide-details
                            color="#808080"
                        >
                        </v-select>
                      </div>
                    </v-col>
                  </v-row>
                  <div :class="getProtectionsContainerClass(check)">
                    <div v-for="protection in check.protections" :key="protection.nr"  class="ml-3">
                      <v-checkbox color="primary" v-model="protection.use"
                                  :label="protection.userText ? protection.userText : protection.text " />
                      <v-textarea
                          v-if="protection.userText && protection.use"
                          color="primary"
                          outlined
                          v-model="protection.text"
                          label="Schutzmaßnahme"
                          validate-on-blur
                          :rules="[ruleRequired($t('utils:rules.required'))]"
                      />
                      <div v-if='protection.use && useCostEstimate' class="horizontal">
                        <v-text-field
                            color="primary"
                            type="number"
                            outlined
                            :full-width="false"
                            class="width200"
                            v-model.number="protection.cost"
                            label="Kosten netto"
                            validate-on-blur
                            :rules="[ruleIsInteger($t('utils:rules.onlynumerictext'))]"
                            @keydown="onKeyDown($event)"
                        />
                        <v-checkbox class="ml-1" color="primary" v-model="protection.usePriceInSum"
                                    label="verwende Preis für Gesamtsumme" />
                      </div>
                    </div>
                  </div>
                  <p class="mb-3 error--text v-messages">{{ showError(check) ? 'Mindestens eine Schutzmaßnahme muss ausgewählt sein': '' }}</p>
                </div>
                <div v-if="check.risk === 'info'">
                  <p v-if="check.note" class="text-pre-wrap">{{check.note}}</p>
                </div>
              </div>
            </div>
          </div>
          <BaseButton
              color="primary"
              btnText="Erzeuge Befund"
              @clickedButton="generateReport()"
          />
          <BaseButton
              :btn-text="$t('buttons:cancel')"
              color="grey"
              @clickedButton="cancelDialog"
          />
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>

</template>

<script>

import BaseButton from "@/modules/core/components/Base/BaseButton.vue";
import { ruleIsInteger, ruleRequired } from "@/modules/core/services/rules";
import PdfUtils from "@/modules/core/services/pdf-utils";
import Utils from "@/modules/core/services/utils";
import GbuResultService from "@/modules/book/services/gbu-result-service";
import LoadingDialog from "@/modules/core/components/Loader/LoadingDialog.vue";
export default {
  name: "FacilityGbuReportDialogForm.vue",
  components: {
    BaseButton,
    LoadingDialog,
  },
  data() {
    return {
      dialog: false,
      useCostEstimate: false,
      data: null,
      showValidation: false,
      gbuId: null,
      protVersion: null,

      changeRisk: 'selected',
      gbuClient: '',
      isSupportDialog: false,
      allProtections: null,
    }
  },
  methods: {
    cancelDialog() {
      this.dialog = false;
    },

    openSupportReportDialog(gbu, previousGbuClient) {
      this.isSupportDialog = true;
      this.dialog = true;
      this.gbuId = gbu.id;
      const protectionsJson = this.getProtectionJson(gbu);
      this.allProtections = JSON.parse(protectionsJson);
      this.allProtections.gbuClient = previousGbuClient ? previousGbuClient : '';
    },

    openDialog(gbu, withCostEstimate, oldProtections, previousGbuClient) {
      this.isSupportDialog = false;
      this.dialog = true;
      this.useCostEstimate = withCostEstimate;
      this.gbuId = gbu.id;
      this.data = [];
      const protectionsJson = this.getProtectionJson(gbu);
      const protections = JSON.parse(protectionsJson);
      this.protVersion = protections.protVersion;
      this.gbuClient = previousGbuClient ? previousGbuClient : '';
      this.allProtections = null;

      const gbuProt = protections.gbuProtections;
      this.getProtectionFields(gbuProt, gbu.chapters);
      const scProtections = protections.scProtections;
      this.getProtectionFields(scProtections, gbu.surroundingsChapters);

      for (const chapter of this.data) {
        for (const check of chapter.checks) {
          if (check.protections.length === 1) {
            check.protections[0].use = true;
          }
        }
      }

      if (oldProtections) {
        //console.log(JSON.stringify(oldProtections, null, 2)); // debug info stored protections
        if (oldProtections.protVersion === this.protVersion) {
          this.gbuClient = oldProtections.gbuClient;
          let protIndex = 0;
          let riskIndex = 0;
          let oldProt = oldProtections.protections;
          let oldRisk = oldProtections.risk;
          for (const chapter of this.data) {
            for (const check of chapter.checks) {
              if (oldRisk && riskIndex < oldRisk.length ) {
                if(oldRisk[riskIndex].nr === check.nr) {
                  check.uiRisk = oldRisk[riskIndex].risk;
                  riskIndex++;
                }
              }
              for (const prot of check.protections) {
                if (protIndex >= oldProt.length) {
                  break;
                }
                let oldValue = oldProt[protIndex];
                if (prot.nr === oldValue.nr) {
                  prot.use = true;
                  prot.text = oldValue.text;
                  if (oldProtections.hasCostEstimate) {
                    prot.cost = oldValue.cost;
                    prot.usePriceInSum = oldValue.incSum;
                  }
                  protIndex++;
                }
              }
            }
          }
        }
      }
      // console.log( JSON.stringify(this.data, null, 2)); // debug info edit-data
    },
    getUiRisk(risk) {
      if(!risk || risk.toLowerCase().startsWith('niedrig')) {
        return 'niedrig';
      }
      if (risk.toLowerCase().startsWith('mittel')) {
        return 'mittel';
      }
      if (risk.toLowerCase().startsWith('hoch')) {
        return 'hoch';
      }
      return 'niedrig';
    },
    riskRemark(risk) {
      if (!risk) {
        return '';
      }
      if (risk.endsWith('¹') || risk.endsWith('*')) {
        return '¹ Die eingetragene Prioritätsstufe kann um eine Stufe niedriger eingetragen werden.'
      }
      if (risk.endsWith('²')) {
        return '² Die eingetragene Prioritätsstufe kann um zwei Stufen niedriger eingetragen werden.'
      }
      if (risk.endsWith('³')) {
        return '³ Je nach Errichtungsstand kann die eingetragene Prioritätsstufe niedriger eingetragen werden.'
      }
      return '';
    },
    getRiskColor(risk) {
      if (!risk) {  // risk not set - use same color as 'niedrig'
        return '#ffff0080'  // yellow with 50% transparency
      }
      if (risk.toLowerCase().startsWith('hoch')) {
        return '#ff000080'  // red with 50% transparency
      }
      if (risk.toLowerCase().startsWith('mittel')) {
        return '#ff800080'  // orange with 50% transparency
      }
      if (risk.toLowerCase().startsWith('niedrig')) {
        return '#ffff0080'  // yellow with 50% transparency
      }
      return '#ffffff00'; // transparent
    },
    getProtectionFields(gbuProt, chapters) {
      let protIndex = 0;
      let currentChapter = null;
      let currentCheck = null;

      for (const chapter of chapters) {
        let nrRisks = chapter.countHighRisk + chapter.countMediumRisk + chapter.countLowRisk + chapter.countNotSet;
        if (nrRisks > 0) {
          for (const check of chapter.checks) {
            if (check.isRisk !== 'ja' && check.risk !== 'info') { // 'nein' or 'nicht zutreffend' und keine cybersecurity info
              continue;
            }

            if (this.data.length === 0 || this.data[this.data.length - 1].title !== chapter.title) {
              // add new chapter
              const dataChapter = {nr: chapter.nr, title: chapter.title, checks: []};
              this.data.push(dataChapter);
              currentChapter = dataChapter;
            }
            let checkNr = check.nr;
            if (check.risk === 'info') {  // special case for cyber-security with info-fields
              const dataCheck = {nr: check.nr, check: check.check, protections: [], risk: check.risk, note: check.note, sect: check.sect}
              currentChapter.checks.push(dataCheck);
              currentCheck = dataCheck;
              continue;
            }
            // search for associated protections for check beginning with same checkNr
            protIndex =0; // search from beginning, so if protections are not found, does not corrupt following checks
            while (gbuProt.length > protIndex && gbuProt[protIndex].nr.substring(0, checkNr.length) !== checkNr) {
              protIndex++;
            }
            // error handling, when a check contains no protections
            if (protIndex >= gbuProt.length) {
              console.error("GBU data error: missing protections for "+check.nr);
              const dataCheck = {nr: check.nr, check: check.check, protections: [], risk: check.risk,
                note: check.note, sect: check.sect, uiRisk: check.uiRisk ? check.uiRisk : this.getUiRisk(check.risk)}
              currentChapter.checks.push(dataCheck);
              currentCheck = dataCheck;
            }

            // iterate through all matching protections for current check
            while (gbuProt.length > protIndex && gbuProt[protIndex].nr.substring(0, checkNr.length) === checkNr) {
              let checkArray = currentChapter.checks;
              if (checkArray.length === 0 || checkArray[checkArray.length - 1].nr !== checkNr) {
                // add new check
                const dataCheck = {nr: check.nr, check: check.check, protections: [], risk: check.risk,
                  note: check.note, sect: check.sect, uiRisk: check.uiRisk ? check.uiRisk : this.getUiRisk(check.risk)}
                currentChapter.checks.push(dataCheck);
                currentCheck = dataCheck;
              }
              const currentProt = gbuProt[protIndex];
              let prot = {nr: currentProt.nr, text: currentProt.text, use: false, cost: null, usePriceInSum: true};
              if (prot.text === '') {
                prot.userText = 'Schutzmaßnahme eingeben:';
              }
              currentCheck.protections.push(prot);
              // console.log(checkNr + " " + gbuProt[protIndex].nr + " " + gbuProt[protIndex].text); // debug info matching check with protection
              protIndex++;
            }

            // additional user input for another user-defined Protection
            if (currentCheck !== null) {
              let prot = {
                nr: currentCheck.nr + ".user", text: '', use: false, cost: null, usePriceInSum: true,
                userText: 'Andere Schutzmaßnahme:'
              };
              currentCheck.protections.push(prot);
            }
          }
        }
      }
    },

    onKeyDown(event) {
      const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
        'Backspace', 'Delete', 'Tab', 'Enter',
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
      if (allowedKeys.indexOf(event.key) >= 0) {
        return false;
      }
      return event.preventDefault();  // ignore all other keys
    },
    generateSupportReport() {
      //console.log( JSON.stringify(this.allProtections, null, 2)); // debug info edit-data
      let isValid = this.$refs.supportForm.validate();
      if (!isValid) {
        return;
      }
      this.$refs.loading.openDialog();

      GbuResultService.generateSupportReport(this.gbuId, JSON.stringify(this.allProtections),
          (result) => {
            this.$refs.loading.closeDialog();
            PdfUtils.openPDFInNewTabSetFileType(result.data);
            this.$emit("update-gbu", this.gbuId);
            this.cancelDialog();
          },
          (error) => {
            this.$refs.loading.closeDialog();
            this.$logger.error("gbu support-report could not be not generated, because: " + error);
            Utils.showFailureSnackbar(this.$t("book/process:overview.report-gen-failed")); // befund konnte nicht generiert werden
          }
      );
    },
    generateReport() {
      // console.log( JSON.stringify(this.data, null, 2)); // debug info edit-data
      this.showValidation = true;
      let isValid = this.$refs.form.validate();  //validates cost- and text input
      // check if at least one protection is set per check-point
      if (isValid) {
        for (let chapter of this.data) {
          if (!isValid) {
            break;
          }
          for (let check of chapter.checks) {
            if (check.risk === 'info') {
              continue;
            }
            let hasProtection = this.hasProtectionSet(check);
            if (!hasProtection) {
              isValid = false;
              break;
            }
          }
        }
      }
      if (!isValid) {
        this.$refs.top.scrollIntoView({behavior: 'smooth'})
        return;
      } else {
        this.$refs.loading.openDialog();
        let selectedProtections = this.generateSelectedProtections();
        // console.log(JSON.stringify(selectedProtections, null, 2)); // debug info selected protections to be stored in db
        GbuResultService.updateGbuAndGenerateReport(this.gbuId, JSON.stringify(selectedProtections),
            (result) => {
              this.$refs.loading.closeDialog();
              PdfUtils.openPDFInNewTabSetFileType(result.data);
              this.$emit("update-gbu", this.gbuId);
              this.cancelDialog();
            },
            (error) => {
              this.$refs.loading.closeDialog();
              this.$logger.error("Gbu-Selected-Protections could not be updated and report not being generated, because: " + error);
              Utils.showFailureSnackbar(this.$t("book/process:overview.report-gen-failed")); // befund konnte nicht generiert werden
            }
        );
      }
    },

    canChangeRisk(check) {
      if (!check) {
        console.log('error')
      }
      return this.changeRisk === 'all' || this.riskRemark(check.risk) || !check.risk
    },

    generateSelectedProtections() {
      let selectedProtections = {
        protVersion: this.protVersion,
        hasCostEstimate: this.useCostEstimate,
        protections: [],
        risk:[],
        gbuClient: this.gbuClient
      }
      for (let chapter of this.data) {
        for (let check of chapter.checks) {
          if (check.risk === 'info') {
            continue;
          }
          if (this.canChangeRisk(check)) {
            selectedProtections.risk.push( {nr:check.nr, risk: check.uiRisk}); // set to value from ui
          }
          else {
            selectedProtections.risk.push( {nr:check.nr, risk: check.risk}); // set to default value
          }
          for (let prot of check.protections) {
            if (prot.use) {
              let newProt = {nr: prot.nr, text: prot.text};
              if (this.useCostEstimate) {
                newProt.cost = +prot.cost;
                newProt.incSum = prot.usePriceInSum === true;
              }
              selectedProtections.protections.push(newProt);
            }
          }
        }
      }
      return selectedProtections;
    },

    getProtectionsContainerClass(check) {
      return this.showError(check) ? "error-border" : "border";
    },

    showError(check) {
      if (!this.showValidation) {
        return false;
      }
      return !this.hasProtectionSet(check);
    },

    hasProtectionSet(check) {
      for (let prot of check.protections) {
        if (prot.use) {
          return true;
        }
      }
      return false;
    },


    ruleIsInteger,
    ruleRequired,
    getProtectionJson(gbu) {
      const formVersion = gbu.formVersion;
      if (formVersion === 1) {
        return this.getProtectionJsonV1();
      }
      else if (formVersion === 2) {
        return this.getProtectionJsonV2();
      }
      else if (formVersion >= 3) {
        // from gbu formVersion 3 onwards, protections are stored within jsonContent.chapters / jsonContent.surroundingsChapters

        let protections = {formVersion:formVersion,protVersion:formVersion,gbuProtections:[], scProtections: []};
        for (const chapter of gbu.chapters) {
          for (const check of chapter.checks) {
            const checkNr= check.nr;
            let startChar = 97;  // char 'a'
            for (const prot of check.protections) {
              const currentNr= checkNr + '.' + String.fromCharCode(startChar);
              startChar++;
              protections.gbuProtections.push( { nr: currentNr, text: prot})
            }
          }
        }
        if (gbu.useSurroundingsConsideration) {
          for (const chapter of gbu.surroundingsChapters) {
            for (const check of chapter.checks) {
              const checkNr= check.nr;
              let startChar = 97;  // char 'a'
              for (const prot of check.protections) {
                const currentNr= checkNr + '.' + String.fromCharCode(startChar);
                startChar++;
                protections.scProtections.push( { nr: currentNr, text: prot})
              }
            }
          }
        }
        return JSON.stringify(protections);
      }
      else {
        return JSON.stringify({"formVersion": 0, "protVersion": 0, "gbuProtections": []});  // no protection-texts
      }
    },

    /**
     * old seperate version 2 of GbuProtections for GBU
     * from version 3 onwards the possible Protections are stored within gbu.jsonContent
     *
     * @returns {string} returns json-string
     */
    getProtectionJsonV2() {
      return `{
  "formVersion": 2,
  "protVersion": 2,
  "gbuProtections": [
    {
      "nr": "1.1.a",
      "text": "technisch: Einbau einer vollwandigen Schachtumwehrung nach EN 81-20:-, Pkt. 5.2.5.2.2.1"
    },
    {
      "nr": "1.1.b",
      "text": "technisch: Absperren des Gefahrenbereiches"
    },
    {
      "nr": "1.2.a",
      "text": "technisch: Einbau einer durchbrochenen Schachtumwehrung nach EN 81-20:-, Pkt. 5.2.5.2.3"
    },
    {
      "nr": "1.2.b",
      "text": "technisch: Einbau einer vollwandigen Schachtumwehrung nach EN 81-20:-, Pkt. 5.2.5.2.2.1"
    },
    {
      "nr": "1.3.a",
      "text": "technisch: Einbau einer Fangvorrichtung am Gegengewicht oder Ausgleichgewicht nach EN 81-20:-, Pkt. 5.2.5.4"
    },
    {
      "nr": "1.3.b",
      "text": "organisatorisch: Verschließen/Absperren der betretbaren Räume unter der Schachtgrube"
    },
    {
      "nr": "1.4.a",
      "text": "technisch: Belüftung verbessern durch freie Entlüftungsöffnung im Schachtkopf und Nachströmöffnung im unteren Schachtbereich"
    },
    {
      "nr": "1.4.b",
      "text": "technisch: Einbau einer temperaturgführten maschinellen Be- und Entlüftung."
    },
    {
      "nr": "1.4.c",
      "text": "technisch: technische Funktionen unempfindlich gegen Soneneinstrahlung machen. Verwendung UV-beständiger Matreialien"
    },
    {
      "nr": "2.1 a).a",
      "text": "technisch: Einbau von Fahrkorbtür(en) nach EN 81-20:-, Pkt. 5.3.1.2"
    },
    {
      "nr": "2.1 b).a",
      "text": "technisch: Einbau von Schachttüren nach EN 81-20:-, Pkt. 5.3.1.2"
    },
    {
      "nr": "2.2.a",
      "text": "technisch: Einbau einer Einrichtung nach EN-81-20:, Pkt. 5.3.6.2.2.1 c) und d)"
    },
    {
      "nr": "2.2.b",
      "text": "technisch: Türschließgeschwindigkeit reduzieren"
    },
    {
      "nr": "2.3.a",
      "text": "technisch: Verringerung der Schließenergie, geringere Schließgeschwindigkeit gemäß EN 81-20:-, Pkt. 5.3.6.2.2.1 a"
    },
    {
      "nr": "2.3.b",
      "text": "technisch: Warnsignale beim Schließvorgang anbringen"
    },
    {
      "nr": "2.4.a",
      "text": "technisch: Einbau von Einrichtungen, z.B. Lichtgitter, nach EN 81-20:-, Pkt. 5.3.6.2.2.1 b)"
    },
    {
      "nr": "2.4.b",
      "text": "technisch: Warnsignale beim Schließvorgang anbringen"
    },
    {
      "nr": "2.5.a",
      "text": "technisch: Anpassen der Nutzfläche gemäß EN 81-20:-, Pkt. 5.4.2"
    },
    {
      "nr": "2.5.b",
      "text": "technisch: Einbau einer redundanten Überlastsicherung"
    },
    {
      "nr": "2.6.a",
      "text": "technisch: Einbau einer Beleuchtung nach EN 81-20:-, Pkt. 5.4.10"
    },
    {
      "nr": "2.6.b",
      "text": "technisch: Verhinderung der Fahrt des Aufzuges ohne Fahrkorblicht"
    },
    {
      "nr": "2.7.a",
      "text": "technisch: Einbau einer Notbeleuchtung nach EN 81-20:-, Pkt. 5.4.10.4"
    },
    {
      "nr": "2.7.b",
      "text": "technisch: Energiespeichernde Aufkleber verwenden"
    },
    {
      "nr": "2.8.a",
      "text": "technisch: Schaffung einer Fahrkorbbelüftung nach EN 81-20:-, Pkt. 5.4.9"
    },
    {
      "nr": "2.8.b",
      "text": "technisch: Verhinderung der Fahrt des Aufzuges ohne Fahrkorbbelüftung"
    },
    {
      "nr": "2.8.c",
      "text": "organisatorisch: Nutzerkreis durch Warnhinweise informieren"
    },
    {
      "nr": "2.9.a",
      "text": "technisch: Nachrüsten aller technischer Maßnahmen gemäß EN 81-82 (z.B. Handlauf, Größe der Taster)"
    },
    {
      "nr": "2.9.b",
      "text": "technisch: Nutzerkreis einschränken (durch Warnhinweise oder Barrieren)"
    },
    {
      "nr": "2.10.a",
      "text": "technisch: Aufsetzvorrichtung für die Aufzugskabine nachrüsten"
    },
    {
      "nr": "2.10.b",
      "text": "technisch: Warnschilder anbringen oder den Staplerbetrieb untersagen"
    },
    {
      "nr": "3.1.a",
      "text": "technisch: Geländer und / oder einen sicheren Aufstieg ermöglichen gemäß EN 81-20:-, Pkt. 5.2.2.5"
    },
    {
      "nr": "3.1.b",
      "text": "technisch: Anbringen von Leitern gemäß EN 81-20:-, Pkt. 5.2.2.5"
    },
    {
      "nr": "3.2.a",
      "text": "technisch: Beleuchtung gemäß EN 81-20:-, Pkt. 5.2.1.4.2"
    },
    {
      "nr": "3.2.b",
      "text": "technisch: ausreichende mobile Arbeitsplatzbeleuchtung bereitstellen."
    },
    {
      "nr": "3.3.a",
      "text": "technisch: Einbau von Einrichtungen gemäß EN 81-20:-, Pkt. 5.2.6.3.2.4"
    },
    {
      "nr": "3.3.b",
      "text": "technisch: Einbau von Einrichtungen gemäß EN 81-20:-, Pkt. 5.2.6.3.2.5"
    },
    {
      "nr": "3.4.a",
      "text": "technisch: Rutschhemmende Oberflächen gemäß EN 81-20:-, Pkt. 5.2.1.9 herstellen"
    },
    {
      "nr": "3.4.b",
      "text": "technisch: Im Bedarfsfall entsprechende Schuhe bereithalten"
    },
    {
      "nr": "3.5.a",
      "text": "technisch: Sicherstellung der Freiräume durch technische Einrichtungen z.B. gemäß EN-81:-, Pkt. 5.2.6.3.2.1, 5.2.6.3.2.2, 5.2.6.3.2.3, 5.2.6.4.2.1, 5.2.6.4.2.2, z.B. die Raumhöhe < 2,1m"
    },
    {
      "nr": "3.5.b",
      "text": "technisch: Sicherung beweglicher mechanischer Teile durch Abtrennung nach EN ISO 13857:2008 4.2.4.1"
    },
    {
      "nr": "3.6.a",
      "text": "technisch: Einbau von Bremsen nach EN 81-20:-, Pkt. 5.9.2.2.2"
    },
    {
      "nr": "3.6.b",
      "text": "technisch: Bremsüberwachung nachrüsten"
    },
    {
      "nr": "3.7.a",
      "text": "technisch: Einbau eines Notbetriebssystem nach EN 81-20:-, Pkt. 5.9.2.3.1 a oder 5.9.3.9"
    },
    {
      "nr": "3.7.b",
      "text": "technisch: Einbau eines Notbetriebssystem nach EN 81-20:-, Pkt. 5.9.2.3.1 b"
    },
    {
      "nr": "3.8.a",
      "text": "technisch: Einbau einer Gegensprechanlage oder einer ähnlichen Einrichtung gemäß EN-81:-, Pkt. 5.12.3.2"
    },
    {
      "nr": "3.8.b",
      "text": "organisatorisch: Im Bedarfsfall zusätzliches Personal zur Kontaktaufnahme am nächsten Schachtzugang mit mobilen Geräten vorgehalten"
    },
    {
      "nr": "3.9 a).a",
      "text": "technisch: Hinweis Schilder anbringen"
    },
    {
      "nr": "3.9 b).a",
      "text": "technisch: Hinweis Schilder anbringen"
    },
    {
      "nr": "3.9 c).a",
      "text": "technisch: Hinweis Schilder anbringen"
    },
    {
      "nr": "3.9 d).a",
      "text": "technisch: Typenschild anbringen"
    },
    {
      "nr": "3.9 e).a",
      "text": "technisch: Hinweis Schilder anbringen"
    },
    {
      "nr": "3.9 f).a",
      "text": "technisch: GELBE Farbe nur für Notruftaster verwenden"
    },
    {
      "nr": "3.9 g).a",
      "text": "technisch: Betriebsanleitungen und Aufzugbuch bereitstellen"
    },
    {
      "nr": "3.10.a",
      "text": "technisch: Einbau von elektrischen Einrichtungen mit Gehäusen gemäß EN 81-20:-, Pkt. 5.10.1.2.2"
    },
    {
      "nr": "3.10.b",
      "text": "technisch: Abdeckungen anbringen"
    },
    {
      "nr": "3.11.a",
      "text": "technisch: Einbau von Schutz gegen Überlastung/Überhitzung des Aufzugsmotors gemäß EN 81-20:-, Pkt. 5.10.4"
    },
    {
      "nr": "3.11.b",
      "text": "technisch: elektr.Sicherheitseinrichtungen zur Abschaltung bei Überlast vorsehen"
    },
    {
      "nr": "3.12.a",
      "text": "technisch: Umbau der Antriebseinheit auf zwei Auflagerpunkte"
    },
    {
      "nr": "3.12.b",
      "text": "technisch: Ultraschallprüfung in Abständen aller 5 Jahre"
    },
    {
      "nr": "3.13.a",
      "text": "technisch: Einbau eines Erdfehlerschutzes EN 81-20:-, Pkt. 5.11.1.4"
    },
    {
      "nr": "3.13.b",
      "text": "technisch: Einbau einer fehlersicheren elektrischen Schaltung"
    },
    {
      "nr": "3.14.a",
      "text": "technisch: Einbau eines abschließbaren Hauptschalters nach EN 81-20:-, Pkt. 5.10.5"
    },
    {
      "nr": "3.14.b",
      "text": "technisch: vorhandenen Hauptschalter so gestalten, dass er gegen Wiedereinschalten gesichert werden kann"
    },
    {
      "nr": "3.15.a",
      "text": "technisch: Einbau einer Einrichtung zur Anhalte- und Nachregu-liergenauigkeit nach EN 81-20:-, Pkt. 5.12.1.1.4"
    },
    {
      "nr": "3.15.b",
      "text": "organisatorisch: Nutzerkreis einschränken, Nutzung bei gefährlicher Stufenbildung untersagen, Warnhinweise anbringen und Warnsignale im Stufenbereicht anbringen"
    },
    {
      "nr": "3.16.a",
      "text": "technisch: Nachrüsten der elektrischen Überwachung der Tragseile nach EN 81-20:-, Pkt. 5.5.5.3"
    },
    {
      "nr": "3.16.b",
      "text": "organisatorisch: Wartungsintervalle verkürzen"
    },
    {
      "nr": "3.17.a",
      "text": "technisch: Einbau einer Laufzeitüberwachung nach EN 81-20:-, Pkt. 5.9.2.7 oder 5.9.3.10"
    },
    {
      "nr": "3.17.b",
      "text": "technisch: Einbau von Schutz gegen Überlastung/Überhitzung des Aufzugsmotors gemäß EN 81-20:-, Pkt. 5.10.4"
    },
    {
      "nr": "3.18.a",
      "text": "technisch: Nachrüsten eines Phasenumkehrschutzes des Hauptstromkreises gemäß EN 81-20:-, Pkt. 5.11.1.2j"
    },
    {
      "nr": "3.18.b",
      "text": "technisch: Nachrüsten eines Frequenzumrichters/Anlaufstrombergrenzers"
    },
    {
      "nr": "3.19.a",
      "text": "technisch: Anbringen von Kennzeichnungen gemäß EN 81-20:-, Pkt. 5.10.6.3.5"
    },
    {
      "nr": "3.19.b",
      "text": "technisch: Anbringen von Hinweisen zu abweichenden Kennzeichnungen gemäß EN 81-20:-, Pkt. 5.10.6.3.5"
    },
    {
      "nr": "3.20.a",
      "text": "technisch: Einbau einer Kolbenabsinkverhinderung gemäß EN 81-20:-, Pkt. 5.9.3.9.1.5"
    },
    {
      "nr": "3.20.b",
      "text": "organisatorisch: Warnschilder anbringen"
    },
    {
      "nr": "3.21.a",
      "text": "technisch: Nachrüsten der Handpumpe nach EN 81-20:-, Pkt. 5.9.3.9.2.1"
    },
    {
      "nr": "3.21.b",
      "text": "technisch: elektr. Einrichtung für den Notbetrieb nachrüsten"
    },
    {
      "nr": "3.22.a",
      "text": "technisch: Nachrüsten des Absperrventils gemäß EN 81-20:-, Pkt. 5.9.3.5.1"
    },
    {
      "nr": "3.22.b",
      "text": "organisatorisch: Warnschilder anbringen"
    },
    {
      "nr": "3.23.a",
      "text": "technisch: Entfernen der schädlichen Stoffe gemäß EN 81-20:-, Pkt. 0.4.3.e"
    },
    {
      "nr": "3.23.b",
      "text": "technisch: entsprechende Schutzeinrichtungen werden vorgehalten"
    },
    {
      "nr": "3.24.a",
      "text": "technisch: Einbau eines geeigneten Schutzes gegen unkontrol-lierte Aufwärtsbewegung nach EN 81-81:-, Pkt. 5.6.6."
    },
    {
      "nr": "3.24.b",
      "text": "technisch: Einbau einer Bremse nach DIN EN 81-20, 5.9.2.2.2 mit Bremsüberwachung"
    },
    {
      "nr": "3.25.a",
      "text": "technisch: Einbau von Mitteln zum Stillsetzen des Triebwerks und Überwachung seines Stillstandes nach EN 81-20:-, Pkt. 5.9.2.5"
    },
    {
      "nr": "3.25.b",
      "text": "technisch: Einbau von Mitteln zum Stillsetzen des Triebwerks und Überwachung seines Stillstandes nach EN 81-20:-, Pkt. 5.9.3.4"
    },
    {
      "nr": "4.1.a",
      "text": "technisch: Einbau einer Schachtbeleuchtung nach EN 81-20:-, Pkt. 5.2.1.4.1"
    },
    {
      "nr": "4.1.b",
      "text": "technisch: Vorhalten einer ausreichenden mobilen Arbeitsplatzbeleuchtung"
    },
    {
      "nr": "4.2.a",
      "text": "technisch: Einbau einer Notbeleuchtung nach EN 81-20:-, Pkt. 5.4.10.4"
    },
    {
      "nr": "4.2.b",
      "text": "technisch: Energiespeichernde Aufkleber verwenden"
    },
    {
      "nr": "4.3 a).a",
      "text": "technisch: Verstärkung des Fahrkorbdaches und der Notklappe nach EN 81-20:-, Pkt. 5.4.7.1.a"
    },
    {
      "nr": "4.3 a).b",
      "text": "technisch: Absperrung/Abdeckung oder Einschränkung des Nutzerkreises bzw. –ortes, Anbringen von Hinweisschildern"
    },
    {
      "nr": "4.3 a).c",
      "text": "technisch: Rutschhemmende Oberfläche verwirklichen "
    },
    {
      "nr": "4.3 b).a",
      "text": "technisch: Verstärkung des Fahrkorbdaches und der Notklappe nach EN 81-20:-, Pkt. 5.4.7.1.a"
    },
    {
      "nr": "4.3 b).b",
      "text": "technisch: Absperrung/Abdeckung oder Einschränkung des Nutzerkreises bzw. –ortes, Anbringen von Hinweisschildern"
    },
    {
      "nr": "4.4.a",
      "text": "technisch: Verringerung des freien Abstandes zwischen dem äußeren Rand des Fahrkorbdachs und angrenzenden Wand auf 0,3 m"
    },
    {
      "nr": "4.4.b",
      "text": "technisch: Einbau einer Umwehrung auf dem Fahrkorbdach nach EN 81-20:-, Pkt. 5.4.7.4 oder"
    },
    {
      "nr": "4.4.c",
      "text": "technisch: Einbau einer Umwehrung auf dem Fahrkorbdach nach EN 81-21:2018, Pkt. 5.6"
    },
    {
      "nr": "4.5.a",
      "text": "technisch: Einbau einer Verriegelung der Notklappe nach EN 81-20:-, Pkt. 5.4.6.3"
    },
    {
      "nr": "4.5.b",
      "text": "technisch: Sicherstellen, dass der Aufzug nur mit sicher verschlossenen Notklappe fahren kann"
    },
    {
      "nr": "4.6 a).a",
      "text": "technisch: Zusatzeinrichtungen zum Schaffen des Schutzraumes gemäß EN 81-20:-, Pkt. 5.2.5.7 nachrüsten, z.B. Fahrwegsbegrenzung"
    },
    {
      "nr": "4.6 a).b",
      "text": "technisch: Maßnahmen zur Schaffung von Schutzräumen und Freiräumen nach EN 81-21, Pkt. 5.5"
    },
    {
      "nr": "4.6 b).a",
      "text": "technisch: Schilder anbringen"
    },
    {
      "nr": "4.7.a",
      "text": "technisch: Einbau von Schachttüren nach EN 81-58, wenn die bestehenden Schachttüren nicht den Anforderungen der vorherigen nationalen Brandvorschriften erfüllen"
    },
    {
      "nr": "4.7.b",
      "text": "technisch: andere bauliche Maßnahmen, z.B. zusätzliche Tür in genügend großen Abstand zur bestehenden Schachttür"
    },
    {
      "nr": "4.8 a).a",
      "text": "technisch: Einbau von Einrichtungen mit Angabe der Betriebslast nach EN 81-20:-, Pkt. 5.2.1.7"
    },
    {
      "nr": "4.8 a).b",
      "text": "technisch: Prüfen und Anzeigen der sicheren Betriebslast bestehender Einrichtungen"
    },
    {
      "nr": "4.8 b).a",
      "text": "technisch: Einbau von Einrichtungen mit Angabe der Betriebslast nach EN 81-20:-, Pkt. 5.2.1.7"
    },
    {
      "nr": "4.8 b).b",
      "text": "technisch: Prüfen und Anzeigen der sicheren Betriebslast bestehender Einrichtungen"
    },
    {
      "nr": "4.9.a",
      "text": "technisch: Einbau einer Kontrolle der Beladung nach EN 81-20:-, Pkt. 5.12.1.2"
    },
    {
      "nr": "4.9.b",
      "text": "organisatorisch: eingeschränkter Nutzerkreis, Schulung/Einweisung, Warnschilder anbringen"
    },
    {
      "nr": "4.10.a",
      "text": "technisch: Verstäkung der Schachtwände gemäß EN 81-20:-, Pkt. 5.2.1.8.2"
    },
    {
      "nr": "4.10.b",
      "text": "organisatorisch: ggf. Absperrungen einrichten, Nutzungsumfeld beschränken, Warnschilder anbringen"
    },
    {
      "nr": "4.11.a",
      "text": "technisch: Abweiser anbringen nach EN 81-20:-, Pkt. 5.2.5.2.2.2"
    },
    {
      "nr": "4.11.b",
      "text": "organisatorisch: Warnschilder anbringen"
    },
    {
      "nr": "4.12.a",
      "text": "technisch: Einbau von Einrichtungen zur Verringerung des Abstandes nach EN 81-20:-, Pkt. 5.2.5.3.1"
    },
    {
      "nr": "4.12.b",
      "text": "technisch: Einbau einer Verriegelung an der Fahrkorbtür nach EN 81-20:-, Pkt. 5.3.9.2"
    },
    {
      "nr": "4.13.a",
      "text": "technisch: Personen müssen daran gehindert werden, sich zwischen der geschlossenen Fahrkorbtür und der Schachttür nach EN 81-20:-, Pkt. 5.3.4.3 aufzuhalten"
    },
    {
      "nr": "4.13.b",
      "text": "technisch: Einbau einer neuen Kabinen- und Schachttürkombination"
    },
    {
      "nr": "4.15.a",
      "text": "technisch: Nottüren im Aufzugsschacht einbauen gemäß EN 81-20:-, Pkt. 5.2.3.1"
    },
    {
      "nr": "4.15.b",
      "text": "organisatorisch: bes. Maßnahmen für die Personenbefreiung festlegen, Feuerwehr über diese Besonderheit informieren"
    },
    {
      "nr": "4.16.a",
      "text": "technisch: Einbau einer Abtrennung über die volle Höhe nach EN 81-20:-, Pkt. 5.2.5.5.2.2"
    },
    {
      "nr": "4.16.b",
      "text": "technisch: Arbeitsraum auf der Kabine einschränken; Gruppenaufzug für die Zeit der Arbeiten ausschalten"
    },
    {
      "nr": "4.17.a",
      "text": "technisch: Einbau von Einrichtungen in Übereinstimmung mit EN 81-20:-, Pkt. 5.3.11"
    },
    {
      "nr": "4.17.b",
      "text": "organisatorisch: Warnhinweise anbringen, Wartungszyklus verringern."
    },
    {
      "nr": "4.18.a",
      "text": "technisch: Einbau von Schachttürverriegelungen nach EN 81-20:-, Pkt. 5.3.9.1"
    },
    {
      "nr": "4.18.b",
      "text": "technisch: Stilllegung von Schachtzugängen"
    },
    {
      "nr": "4.19.a",
      "text": "technisch: Einbau von festen Führungsschienen nach EN 81-20:-, Pkt. 5.7"
    },
    {
      "nr": "4.19.b",
      "text": "technisch: Einbau von festen Führungsschienen nach EN 81-21:2018, Pkt. 5.4.3"
    },
    {
      "nr": "4.20.a",
      "text": "technisch: Bypass nach EN 81-20:-, Pkt. 5.12.1.8 nachrüsten"
    },
    {
      "nr": "4.20.b",
      "text": "organisatorisch: Warnschilder anbringen"
    },
    {
      "nr": "4.21.a",
      "text": "technisch: Einbau einer Schließeinrichtung in Übereinstimmung mit dem letzten Absatz aus EN 81-20:-, Pkt. 5.3.9.3.4, z.B. Feder"
    },
    {
      "nr": "4.21.b",
      "text": "technisch: Einbau einer Schließeinrichtung in Übereinstimmung mit dem letzten Absatz aus EN 81-20:-, Pkt. 5.3.9.3.4, z.B. Gewicht"
    },
    {
      "nr": "4.22.a",
      "text": "technisch: Sicherstellen, dass die Schachttür erst entriegelt wird, bis die Fahrkorbtür vollständig geöffnet ist"
    },
    {
      "nr": "4.22.b",
      "text": "technisch: Sicherstellen, dass die Fahrkorbtür erst dann zu schließen beginnt, wenn die Schachttür geschlossen ist"
    },
    {
      "nr": "4.23.a",
      "text": "technisch: Vorsehen einer vollständigen Schachtumwehrung oder"
    },
    {
      "nr": "4.23.b",
      "text": "technisch: Vorsehen eines Schutzes, um die Schachttürverriegelung nach EN 81-21:2018 Pkt. 5.2 b"
    },
    {
      "nr": "4.24 a.a",
      "text": "technisch: Einbau einer Inspektionssteuerung nach EN 81-20:-, Pkt. 5.4.8"
    },
    {
      "nr": "4.24 a.b",
      "text": "technisch: Wartung vom Kabinendach ist mit anderen Mitteln durchzuführen"
    },
    {
      "nr": "4.24 b.a",
      "text": "technisch: vorhandene Inspektionssteuerung nach EN 81-20:- Pkt. 5.12.1.5.1 umbauen"
    },
    {
      "nr": "4.24 b.b",
      "text": "technisch: vorhandene Inspektionssteuerung nach EN 81-20:-, Pkt. 5.12.1.5.2.4 (Farbe + Symbol) umbauen"
    },
    {
      "nr": "4.25.a",
      "text": "technisch: Position Notbremsschalter anpassen nach EN 81-20: Pkt. 5.4.8."
    },
    {
      "nr": "5.1.a",
      "text": "technisch: Einbau einer Zugangstür zur Schachtgrube nach EN 81-20:-, Pkt. 5.2.2.4"
    },
    {
      "nr": "5.1.b",
      "text": "technisch: Einbau einer Leiter nach EN 81-20:-, Pkt. 5.2.2.4"
    },
    {
      "nr": "5.2.a",
      "text": "technisch: Einbau einer Fahrkorbschürze nach EN 81-20:-, Pkt. 5.4.5"
    },
    {
      "nr": "5.2.b",
      "text": "technisch: Einbau einer Fahrkorbschürze nach EN 81-21:2018 Pkt. 5.8"
    },
    {
      "nr": "5.3.a",
      "text": "technisch: Vorsehen von Schließeinrichtungen nach EN 81-20:-, Pkt. 5.2.3.3 b)"
    },
    {
      "nr": "5.3.b",
      "text": "technisch: Vorsehen von Schließeinrichtungen nach EN 81-20:-, Pkt. 5.2.3.3 c)"
    },
    {
      "nr": "5.4.a",
      "text": "technisch: Einbau einer Fahrkorbtürdrossel nach EN 81-20:- Pkt. 5.3.15.2"
    },
    {
      "nr": "5.4.b",
      "text": "technisch: Einbau einer Fahrkorbverriegelung nach EN 81-20:- Pkt. 5.3.9.2"
    },
    {
      "nr": "5.5.a",
      "text": "technisch: Schachtwände gemäß EN 81-20:-, Pkt. 5.2.5.3.2 anpassen"
    },
    {
      "nr": "5.5.b",
      "text": "technisch: Vorsprünge an Schachtwänden nach EN 81-20:-, Pkt. 5.2.5.3.2 vermeiden"
    },
    {
      "nr": "5.6.a",
      "text": "technisch: Einbau einer Gegengewichts- oder Ausgleichgewichts-abtrennung nach EN 81-20:-, Pkt. 5.2.5.5.1"
    },
    {
      "nr": "5.6.b",
      "text": "organisatorisch: Warnhinweise anbringen"
    },
    {
      "nr": "5.7.a",
      "text": "technisch: Hinweisschild an bzw. in der Nähe der Gegenge-wichtsabtrennung nach EN 81-20:- Pkt. 5.2.5.7.1 anbringen"
    },
    {
      "nr": "5.7.b",
      "text": "organisatorisch: Wartungsanweisung überarbeiten"
    },
    {
      "nr": "5.8.a",
      "text": "technisch: Einbau einer Inspektionssteuerung nach EN 81-20:-, Pkt. 5.2.1.5.1 b)"
    },
    {
      "nr": "5.8.b",
      "text": "organisatorisch: Wartung mit anderen Mitteln durchführen"
    },
    {
      "nr": "5.9.a",
      "text": "technisch: vorhandene Inspektionssteuerung nach EN 81-20:- Pkt. 5.12.1.5.1 umbauen"
    },
    {
      "nr": "5.9.b",
      "text": "technisch: vorhandene Inspektionssteuerung nach EN 81-20:-, Pkt. 5.12.1.5.2.4 (Farbe + Symbol) umbauen"
    },
    {
      "nr": "5.10.a",
      "text": "technisch: Einbau einer Notrufeinrichtung nach EN 81-20:-, Pkt. 5.2.1.6"
    },
    {
      "nr": "5.10.b",
      "text": "organisatorisch: 2-Mann-Wartung"
    },
    {
      "nr": "5.11.a",
      "text": "technisch: Einbau einer Abtrennung in der Schachtgrube nach EN 81-20:-, Pkt. 5.2.5.5.2"
    },
    {
      "nr": "5.11.b",
      "text": "technisch: Abschaltung aller anderen Aufzüge im gemeinsamen Schacht zur Verhinderung des Anfahrens mit beweglichen Anlagenteilen oder Stillegung/Ausbau eines Aufzuges"
    },
    {
      "nr": "5.12.a",
      "text": "technisch: ölfesten Anstrich nachbessern und Nachweis über eingesetzte Farbe, Datum und Unternehmen anbringen"
    },
    {
      "nr": "5.12.b",
      "text": "technisch: Nachrüsten einer festen Ölwanne"
    },
    {
      "nr": "5.13.a",
      "text": "technisch: Einbau von Puffern unter dem Fahrkorb nach EN 81-20:-, Pkt. 5.8"
    },
    {
      "nr": "5.13.b",
      "text": "technisch: Einbau von Puffern unter dem Gegengewicht nach EN 81-20:-, Pkt. 5.8"
    },
    {
      "nr": "5.13.c",
      "text": "technisch: Einbau von Puffern mit linearer Kennlinie nach EN 81-20:-, Pkt. 5.8"
    },
    {
      "nr": "5.14.a",
      "text": "technisch: Einbau eines Schutzes gegen Verletzungen an Treibscheiben nach EN 81-20:-, Pkt. 5.5.7"
    },
    {
      "nr": "5.14.b",
      "text": "technisch: Einbau eines Schutzes gegen Verletzungen an Seilrollen oder Ketttenrädern nach EN 81-20:-, Pkt. 5.5.7"
    },
    {
      "nr": "5.14.c",
      "text": "technisch: Einbau eines Schutzes gegen Verletzungen am Geschwindigkeitsbegrenzer und Spanngewichtsrollen nach EN 81-20:-, Pkt. 5.5.7"
    },
    {
      "nr": "5.15.a",
      "text": "technisch: Einbau eines Schutzes gegen das Herausspringenvon Seilen / Ketten aus Treibscheiben nach EN 81-20:-, Pkt. 5.5.7"
    },
    {
      "nr": "5.15.b",
      "text": "technisch: Einbau eines Schutzes gegen das Herausspringenvon Seilen / Ketten aus Rollen oder Kettenrädern nach EN 81-20:-, Pkt. 5.5.7"
    },
    {
      "nr": "5.15.c",
      "text": "technisch: Einbau eines Schutzes gegen das Herausspringenvon Seilen / Ketten aus Geschwindigkeitsbegrenzern und Spanngewichtsrollen nach EN 81-20:-, Pkt. 5.5.7"
    },
    {
      "nr": "5.16.a",
      "text": "technisch: Einbau eines Schutzes gege das Eindringen von Fremdkörpern von Seilen und Seilscheiben nach EN 81-20:-, Pkt. 5.5.7"
    },
    {
      "nr": "5.16.b",
      "text": "technisch: Einbau eines Schutzes gege das Eindringen von Fremdkörpern von Ketten und Kettenrädern nach EN 81-20:-, Pkt. 5.5.7"
    },
    {
      "nr": "5.16.c",
      "text": "technisch: Einbau eines Schutzes gege das Eindringen von Fremdkörpern an Spanngewichtsrollen nach EN 81-20:-, Pkt. 5.5.7"
    },
    {
      "nr": "5.17 a).a",
      "text": "technisch: Einbau des Notbremsschalters nach EN 81-20:-, Pkt. 5.12.1.11.1 a)"
    },
    {
      "nr": "5.17 a).b",
      "text": "technisch: Einbau von Notbremsschaltern nach EN 81-20:-, Pkt. 5.12.1.11.1 e)"
    },
    {
      "nr": "5.17 a).c",
      "text": "technisch: Einbau von Notbremsschaltern nach EN 81-20:-, Pkt. 5.12.1.11.1 außer a) und e)"
    },
    {
      "nr": "5.17 b).a",
      "text": "technisch: Anpassung der Position und Anzahl der Notbremsschalter in der Grube"
    },
    {
      "nr": "5.18.a",
      "text": "technisch: Einbau eines Schlaffseilschalters gemäß EN 81-20:-, Pkt. 5.6.2.2.1.6.c"
    },
    {
      "nr": "5.18.b",
      "text": "technisch: alternative Überwachung der unzulässigen Seillängung"
    },
    {
      "nr": "5.19.a",
      "text": "technisch: Einbau eines Schlaffseilschalters gemäß EN 81-20:-, Pkt. 5.5.5.3"
    },
    {
      "nr": "5.19.b",
      "text": "technisch: alternative Überwachung der unzulässigen Seillängung"
    },
    {
      "nr": "5.20 a).a",
      "text": "technisch: Einbau von Notendschaltern gemäß EN 81-20:-, Pkt. 5.12.2"
    },
    {
      "nr": "5.20 a).b",
      "text": "technisch: alternative Systeme zur Signalisation der Grenze des max. möglichen Fahrweges"
    },
    {
      "nr": "5.20 b).a",
      "text": "technisch: Einbau von Notendschaltern gemäß EN 81-20:-, Pkt. 5.12.2"
    },
    {
      "nr": "5.20 b).b",
      "text": "technisch: alternative Systeme zur Signalisation der Grenze des max. möglichen Fahrweges"
    },
    {
      "nr": "5.21.a",
      "text": "technisch: Einbau einer Kombination von Sicherheitseinrichtungen mittels Geschwindigkeitsbegrenzers oder Schlaffseil gemäß EN 81-20:-, Tabelle 12"
    },
    {
      "nr": "5.21.b",
      "text": "technisch: Einbau einer Kombination von Sicherheitseinrichtungen mittels Leitungsbruchventils oder Drossel gemäß EN 81-20:-, Tabelle 12"
    },
    {
      "nr": "5.22.a",
      "text": "technisch: Prüfen, ob alle bestehenden Komponenten für den Schutz gegen freien Fall und Abwärtbewegung mit überhöhter Geschwindigkeit angemesen und kompatibel sind oder, wenn nicht"
    },
    {
      "nr": "5.22.b",
      "text": "technisch: Einbau eines Schutzes gegen freien Fall und Abwärtsbewegung mit überhöhter Geschwindigkeit nach EN 81-20:- Tabelle 11 oder 12"
    },
    {
      "nr": "5.23.a",
      "text": "technisch: Einbau einer Kombination von Sicherheitseinrichtungen nach EN 81-20:-, Pkt. 5.6.7 für Treibscheibenaufzüge"
    },
    {
      "nr": "5.23.b",
      "text": "technisch: Einbau einer Kombination von Sicherheitseinrichtungen nach EN 81-20:-, Pkt. 5.6.7 für Hydraulikaufzüge"
    },
    {
      "nr": "5.24 a).a",
      "text": "technisch: Zusatzeinrichtungen zum Schaffen des Schutzraumes gemäß EN 81-20:-, Pkt. 5.2.5.8 nachrüsten, z.B. Stützen"
    },
    {
      "nr": "5.24 a).b",
      "text": "technisch: Technische Maßnahmen zur Schaffung von Schutzräumen und Freiräumen nach EN 81-21, Pkt. 5.7"
    },
    {
      "nr": "5.24 b).a",
      "text": "technisch: Hinweis Schilder anbringen"
    },
    {
      "nr": "5.25.a",
      "text": "technisch: Einbau einer elektrischen Sicherheitseinrichtung nach EN 81-20:-, Pkt. 5.2.3.3 d)"
    },
    {
      "nr": "5.25.b",
      "text": "technisch: Verschließen der Zugänge"
    },
    {
      "nr": "5.26.a",
      "text": "technisch: Einbau einer ständig eingebauten Einrichtung nach EN 81-20 Pkt. 5.3.9.3.5 zum Öffnen der Schachttüre"
    },
    {
      "nr": "6.1.a",
      "text": "technisch: Einbau ausreichender Beleuchtung nach EN 81-20:-, Pkt. 5.3.7.1"
    },
    {
      "nr": "6.1.b",
      "text": "technisch: mobile Leuchtmittel vorhalten"
    },
    {
      "nr": "6.2.a",
      "text": "technisch: Einbau von Schutzeinrichtungen nach EN 81-20:-, Pkt. 5.3.6.2.2.1 i)"
    },
    {
      "nr": "6.2.b",
      "text": "technisch: Einbau von Schutzeinrichtungen nach EN 81-20:-, Pkt. 5.3.6.2.2.1 h)"
    },
    {
      "nr": "6.3.a",
      "text": "technisch: Reduzierung der Größe von Schauöffnungen und Er-satz durch Verbundsicherheitsglas nach EN 81-20:-, Pkt. 5.3.7.2.1 a)"
    },
    {
      "nr": "6.3.b",
      "text": "technisch: Ersatz durch einen festen Einsatz sowie Anbringen einer Fahrkorb-Anwesenheisanzeige nach EN 81-20:-, Pkt. 5.3.7.2.1 b)"
    },
    {
      "nr": "6.4.a",
      "text": "technisch: Einbau von Schachttüren nach EN 81-20:-, Pkt. 5.3.5.3"
    },
    {
      "nr": "6.4.b",
      "text": "technisch: Einbau von Fahrkorbtüren nach EN 81-20:-, Pkt. 5.3.5.3"
    },
    {
      "nr": "6.5.a",
      "text": "technisch: Einbau von Schachttüren mit Glas nach EN 81-20:-, Pkt. 5.3.5.3.4 bis 5.3.5.3.7"
    },
    {
      "nr": "6.5.b",
      "text": "technisch: Einbau von Fahrkorbtüren mit Glas nach EN 81-20:-, Pkt. 5.3.5.3.4 bis 5.3.5.3.7"
    },
    {
      "nr": "6.6.a",
      "text": "technisch: Einbau einer Notentriegelungseinrichtungen nach EN 81-20:-, Pkt. 5.3.9.3"
    },
    {
      "nr": "6.6.b",
      "text": "technisch: Sicherstellung auf andere Weise, kleiner Dreikant"
    },
    {
      "nr": "7.1.a",
      "text": "technisch: Maßnahmen gemäß EN 81-77"
    },
    {
      "nr": "7.1.b",
      "text": "technisch: Risikoeinschätzung vornehmen"
    },
    {
      "nr": "7.2.a",
      "text": "technisch: Maßnahmen gemäß EN 81-73"
    },
    {
      "nr": "7.3.a",
      "text": "technisch: Maßnahmen gemäß EN 81-72"
    },
    {
      "nr": "7.4.a",
      "text": "technisch: Maßnahmen gemäß EN 81-71"
    },
    {
      "nr": "7.5.a",
      "text": "technisch: Maßnahmen gemäß EN 81-70"
    },
    {
      "nr": "7.6.a",
      "text": "technisch: Zugang zu Räumen und Schachttüren vergrößern"
    },
    {
      "nr": "7.6.b",
      "text": "technisch: Zugang zu Räumen und Schachttüren besser beleuchten"
    },
    {
      "nr": "7.6.c",
      "text": "technisch: Absperrungen/Barrieren einbauen"
    },
    {
      "nr": "7.7.a",
      "text": "technisch: Maßnahmen gegen das Eindringen von Tieren ergreifen (Gitter etc.)"
    },
    {
      "nr": "7.7.b",
      "text": "technisch: Gaswarner"
    },
    {
      "nr": "8.1 b).a",
      "text": "technisch: Erstellung einer GBU zum Thema Cybersicherheit und Umsetzten der notwendigen Maßnahmen"
    },
    {
      "nr": "8.2 b).a",
      "text": "technisch: Erstellung einer GBU zum Thema Cybersicherheit und Umsetzten der notwendigen Maßnahmen"
    },
    {
      "nr": "8.3 b).a",
      "text": "technisch: Erstellung einer GBU zum Thema Cybersicherheit und Umsetzten der notwendigen Maßnahmen"
    }
  ],
  "scProtections": [
    {
      "nr": "B0.a",
      "text": "EN 81-70: Fahrkorbabmessungen, Türbreite. Siehe Tabelle 1: Aufzug für Rollstühle mit Begleitperson (DIN EN 12183) oder elektrischer Rollstuhl der Klassen A, B, C  (DIN EN 12184)"
    },
    {
      "nr": "B1.a",
      "text": "Maßnahmen nach EN 81-70: 5.4.3, Anhang E  durchführen (akustische Signale, optische Anzeigen ≥ 40mm, kontrastreich)"
    },
    {
      "nr": "B2.a",
      "text": "Maßnahmen nach EN 81-70: 5.4.3, Anhang E durchführen (optische Anzeigen ≥ 40mm, kontrastreich)"
    },
    {
      "nr": "B3.a",
      "text": "Maßnahmen nach EN 81-70: 5.4.4.3 Absatz c durchführen (z.B. in öffentlichen Gebäuden)"
    },
    {
      "nr": "B4.a",
      "text": "Erreichbarkeit von Schlüsselschaltern u.ä. sicherstellen. Sicherstellen, dass ein Einschluss ausgeschlossen ist."
    },
    {
      "nr": "C0.a",
      "text": "Einhaltung der EN 81-70: Tabelle 2, ggf. Berücksichtigung Anhang F und G."
    },
    {
      "nr": "C1.a",
      "text": "Einhaltung der EN 81-70: Tabelle 2, ggf. Berücksichtigung Anhang G."
    },
    {
      "nr": "D0.a",
      "text": "Maßnahmen bei z.B. Glasschacht, Außenanlage treffen."
    },
    {
      "nr": "E0.a",
      "text": "EN 81-70: 5.2.2 Hindernisfreier Zugang zu Haltestellen sicherstellen"
    },
    {
      "nr": "F0.a",
      "text": "Arbeitsstättenschutzgesetz (keine Hindernisse im Zugang zu Betriebsräumen)"
    },
    {
      "nr": "F1.a",
      "text": "EN 81-20: 5.2.5.7 und 5.2.5.8"
    },
    {
      "nr": "F2.a",
      "text": "Arbeitsstättenschutzgesetz"
    },
    {
      "nr": "G0.a",
      "text": "Arbeitsstättenschutzgesetz (z.B. Gefahrstoffe), Überwachungskontakte usw."
    },
    {
      "nr": "G1.a",
      "text": "Arbeitsstättenschutzgesetz"
    },
    {
      "nr": "G2.a",
      "text": "Tragfähigkeitsangabe im Beladebereich bei Lastenaufzügen vorhanden? Piktogramme?"
    },
    {
      "nr": "G3.a",
      "text": "Kennzeichnung gem. Gefahrstoffverordnung anbringen"
    },
    {
      "nr": "G4.a",
      "text": "Maßnahmen nach EN 81-70: 5.4.3.4 durchführen"
    },
    {
      "nr": "G5.a",
      "text": "Maßnahmen nach BetrSichV durchführen in Verbindung mit EN 81-28"
    },
    {
      "nr": "G6.a",
      "text": ""
    }
  ]
}`
    },

    getProtectionJsonV1() {
      return `{
  "formVersion": 1,
  "protVersion": 1,
  "gbuProtections": [
    {
      "nr": "1.1.a",
      "text": "Maßnahmen in Übereinstimmung mit  CEN / TS 81-83"
    },
    {
      "nr": "1.2.a",
      "text": "Maßnahmen in Übereinstimmung mit EN81-73"
    },
    {
      "nr": "1.3.a",
      "text": "Maßnahmen in Übereinstimmung mit EN81-77"
    },
    {
      "nr": "1.4.a",
      "text": "Bauteile sind frei von schädlichen Stoffen, z.B. Asbest siehe EN81-20: 0.4.3 e \\na) entfernen von zerfallenen Asbest (z.B. Ersatz des Bremsbelag Materials)"
    },
    {
      "nr": "1.4.b",
      "text": "b)  Keine Arbeiten an Asbest ausführen ==> Warnschild anbringen"
    },
    {
      "nr": "2.1.a",
      "text": "Vorsehen einer / mehrerer Schließeinrichtung (en) nach EN81-20:  5.2.3.3 b) und c)"
    },
    {
      "nr": "2.2.a",
      "text": "Einbau einer elektrischen Sicherheitseinrichtung nach EN81-20:  5.2.3.3 d)"
    },
    {
      "nr": "2.3.a",
      "text": "a) Einbau einer vollwandigen Schachtumwehrung nach EN81-20:  5.2.5.2.2.1, oder"
    },
    {
      "nr": "2.3.b",
      "text": "b) falls gerechtfertigt, Einbau einer durchbrochenen Schachtumwehrung nach EN81-21: 5.2"
    },
    {
      "nr": "2.4.a",
      "text": "a) Einbau einer vollwandigen Schacht Umwehrung oder"
    },
    {
      "nr": "2.4.b",
      "text": "b) Vorsehen eines Schutzes um die Schachttürverriegelung nach EN81-21: 5.2 b"
    },
    {
      "nr": "2.5.a",
      "text": "a) Einbau einer vollwandigen Schachtumwehrung nach EN81-20: 5.2.5.2.2.1, oder"
    },
    {
      "nr": "2.5.b",
      "text": "b) falls gerechtfertigt, Einbau einer durchbrochenen Schachtumwehrung nach EN81-20: 5.2.5.2.3"
    },
    {
      "nr": "2.6.a",
      "text": "Vorsehen geeigneter Mittel nach EN81-20: 5.2.5.3.2, unter jeder Türschwelle"
    },
    {
      "nr": "2.7.a",
      "text": "Einbau einer Fangvorrichtung für das Gegen- oder Ausgleichsgewicht (sicherstellen der Bodentragfähigkeit von 5kg/n³ pro m²) nach EN81-20: 5.2.5.4"
    },
    {
      "nr": "2.8.a",
      "text": "Einbau einer Gegengewichts- / Ausgleichsgewichts-abtrennung  nach EN81-20: 5.2.5.5.1"
    },
    {
      "nr": "2.9.a",
      "text": "Einbau einer Abtrennung in der Schachtgrube nach EN81-20: 5.2.5.5.2"
    },
    {
      "nr": "2.10.a",
      "text": "Einbau einer Abtrennung bis zur vollen Schachthöhe nach EN81-20: 5.2.5.5.2"
    },
    {
      "nr": "2.11.a",
      "text": "a) Einbau von Schutzräumen und Freiräumen nach EN81-20: 5.2.5.7"
    },
    {
      "nr": "2.11.b",
      "text": "b) Vorsehen technischer Maßnahmen zur Schaffung von Schutzräumen und Freiräumen nach EN81-21: 5.5"
    },
    {
      "nr": "2.11.c",
      "text": "c) Kennzeichnung der Schutzräume"
    },
    {
      "nr": "2.12.a",
      "text": "a) Einbau von Schutzräumen und Freiräumen in der Schachtgrube nach EN81-20: 5.2.5.8"
    },
    {
      "nr": "2.12.b",
      "text": "b) Vorsehen technischer Maßnahmen zur Schaffung von Schutzräumen und Freiräumen nach EN81-21: 5.7"
    },
    {
      "nr": "2.12.c",
      "text": "c) Kennzeichnung der Schutzräume"
    },
    {
      "nr": "2.13.a",
      "text": "Vorsehen von Maßnahmen für den Zugang zur Schachtgrube nach EN81-20: 5.2.2.4"
    },
    {
      "nr": "2.14.a",
      "text": "Einbau einer Schachtbeleuchtung nach EN81-20: 5.2.1.4.1"
    },
    {
      "nr": "2.15.a",
      "text": "Einbau eines Notbremsschalters nach EN81-20: 5.2.1.5.1 a)"
    },
    {
      "nr": "2.16.a",
      "text": "Sind keine Fluchtmittel für in den Schacht eingeschlossener Personen vorgesehen, sind eine oder mehrere Notrufauslöseeinrichtungen nach EN81-20: 5.2.1.6 vorzusehen"
    },
    {
      "nr": "2.17.a",
      "text": "a) Vorsehen von Maßnahmen zur Verringerung des Abstands nach EN81-20: 5.2.5.3.1 oder"
    },
    {
      "nr": "2.17.b",
      "text": "b) Einbau vor Verriegelungen an der Fahrkorbtür nach EN81-20: 5.3.9.2"
    },
    {
      "nr": "2.18.a",
      "text": "Personen müssen daran gehindert werden, sich zwischen der geschlossenen Fahrkorbtür und der Schachttür nach EN81-20: 5.3.4.3, aufzuhalten"
    },
    {
      "nr": "2.19.a",
      "text": "Personen müssen daran gehindert werden, zwischen der geschlossenen Fahrkorbtür und der Schachttür nach EN81-20: 5.3.4.2, zu betreten"
    },
    {
      "nr": "3.1.a",
      "text": "Vorsehen von Maßnahmen für den sicheren Zugang Aufstellungsorten von Triebwerks- und Rollenraum nach EN81-20: 5.2.2.5"
    },
    {
      "nr": "3.2.a",
      "text": "Einbau einer elektrischen Beleuchtung nach EN81-20: 5.2.1.4.2"
    },
    {
      "nr": "3.3.a",
      "text": "Einbau von Notbremsschaltern nach EN81-20: 5.2.1.5.2 c)"
    },
    {
      "nr": "3.4.a",
      "text": "a) Prüfen und Anzeigen der sicheren Betriebslast bestehender Einrichtungen oder"
    },
    {
      "nr": "3.4.b",
      "text": "b) Einbau von Aufhängepunkten mit Angabe der sicheren Betriebslast nach EN81-20: 5.2.1.7"
    },
    {
      "nr": "3.5.a",
      "text": "Einbau von rutschhemmendem Boden nach EN81-20: 5.2.1.9"
    },
    {
      "nr": "3.6.a",
      "text": "a) Sicherstellen horizontaler und vertikaler Freiräume nach EN81-20: 5.2.6.3.2.1 a) und b), 5.2.6.3.2.2, 5.2.6.3.2.3, 5.2.6.4.2.1 a) und b) 5.2.6.4.2.2 oder"
    },
    {
      "nr": "3.6.b",
      "text": "b) falls horizontale oder vertikale Freiräume fehlen, Sicherung beweglicher Teile durch Abtrennungen nach EN ISO 13857:2018, 4.2.4.1"
    },
    {
      "nr": "3.7.a",
      "text": "Einbau einer Einrichtung nach EN81-20: 5.2.6.3.2.4, 5.2.6.3.2.5"
    },
    {
      "nr": "3.8.a",
      "text": "Einbau einer Gegensprechanlage oder einer ähnlichen Einrichtung nach EN81-20: 5.12.3.2"
    },
    {
      "nr": "4.1.a",
      "text": "Einbau von vollwandigen Schachttüren nach EN81-20: 5.3.1.2"
    },
    {
      "nr": "4.2.a",
      "text": "Einbau von vollwandigen Fahrkorbtüren nach EN81-20: 5.3.1.2"
    },
    {
      "nr": "4.3.a",
      "text": "Einbau von Schachttüren nach EN81-20: 5.3.5.3"
    },
    {
      "nr": "4.4.a",
      "text": "Einbau von Fahrkorbtüren nach EN81-20: 5.3.5.3"
    },
    {
      "nr": "4.5.a",
      "text": "Einbau von Schachttüren mit Glas nach EN81-20: 5.3.5.3.4, 5.3.5.3.5, 5.3.5.3.6, 5.3,5.3.7"
    },
    {
      "nr": "4.6.a",
      "text": "Einbau von Schachttüren mit Glas nach EN81-20: 5.3.5.3.4, 5.3.5.3.5, 5.3.5.3.6, 5.3,5.3.7"
    },
    {
      "nr": "4.7.a",
      "text": "a) reduzieren der Größe von Schauöffnungen und Ersetzen durch Verbundsicherheitsglas nach EN81:20: 5.3.7.2.1 a) oder"
    },
    {
      "nr": "4.7.b",
      "text": "b) Ersatz durch einen festen Einsatz sowie Anbringen einer Fahrkorbanwesenheitsanzeige  nach EN81-20: 5.3.7.2.1 b)"
    },
    {
      "nr": "4.8.a",
      "text": "Reduzierende Größe von Schauöffnungen und Ersetzen durch Verbundsicherheitsglas nach EN81-20: 5.3.7.2.1 a)"
    },
    {
      "nr": "4.9.a",
      "text": "Einbau von Einrichtungen nach EN81-20: 5.3.6.2.2.1 b)"
    },
    {
      "nr": "4.10.a",
      "text": "Einbau von Einrichtungen nach EN81-20: 5.3.6.2.2.1 c) und d)"
    },
    {
      "nr": "4.11.a",
      "text": "Einbau von Einrichtungen nach EN81-20: 5.3.6.2.2.1 c)"
    },
    {
      "nr": "4.12.a",
      "text": "Einbau von Schutzeinrichtungen gegen das Einziehen von Kinderhänden nach EN81-20: 5.3.6.2.2.1 h) und i)"
    },
    {
      "nr": "4.13.a",
      "text": "Einbau von Beleuchtungen an jedem Schachtzugang nach EN81-20: 5.3.7.1"
    },
    {
      "nr": "4.14.a",
      "text": "Einbau Schachttürverriegelungen nach EN81-20: 5.3.9.1"
    },
    {
      "nr": "4.15.a",
      "text": "Einbau Notentriegelungsdreikant nach EN81-20: 5.3.9.3.1"
    },
    {
      "nr": "4.16.a",
      "text": "Einbau Schließeinrichtung nach EN81-20: 5.3.9.3.4"
    },
    {
      "nr": "4.17.a",
      "text": "Einbau einer Einrichtung nach EN81-20: 5.3.11"
    },
    {
      "nr": "4.18.a",
      "text": "Einbau von Schachttüren nach EN81-58 sowie DIN 18091 wenn die bestehenden Schachttüren nicht die Anforderungen der vorherigen nationalen Brandschutzvorschriften erfüllen"
    },
    {
      "nr": "4.19.a",
      "text": "a) Sicherstellen, dass die Schachttür erst entriegelt wird, bis die Fahrkorbtür vollständig geöffnet ist, und"
    },
    {
      "nr": "4.19.b",
      "text": "b) Sicherstellen, dass die Fahrkorbtür erst dann zu schließen beginnt, wenn die Schachttür geschlossen ist."
    },
    {
      "nr": "4.20.a",
      "text": "a) Einbau von automatisch betriebenen Fahrkorbtüren nach EN81-20: 5.3.6.2.2.1, oder"
    },
    {
      "nr": "4.20.b",
      "text": "b) Einbau von nicht automatisch betriebenen Fahrkorbtüren nach EN81-20: 5.3.6.2.2.2"
    },
    {
      "nr": "4.21.a",
      "text": "a) Einbau einer Einrichtung an einer Fahrkorbtür nach EN81-20: 5.3.15.2, oder"
    },
    {
      "nr": "4.21.b",
      "text": "b) Einbau einer Fahrkorbtürverriegelung nach EN81-20: 5.3.9.2"
    },
    {
      "nr": "5.1.a",
      "text": "a) Abstimmen des Verhältnisses zwischen der Grundfläche des Fahrkorbs und der Nutzlast nach  EN81-20: 5.4.2 oder"
    },
    {
      "nr": "5.2.a",
      "text": "a) Einbau einer Fahrkorbschürze nach EN81-20: 5.4.5 oder"
    },
    {
      "nr": "5.2.b",
      "text": "b) Einbau einer Schürze nach EN81-21: 5.8"
    },
    {
      "nr": "5.3.a",
      "text": "Einbau einer Verriegelung der Notklappe nach EN81-20: 5.4.6.3"
    },
    {
      "nr": "5.4.a",
      "text": "Verstärkung des Fahrkorbdachs und der Notklappe nach EN81-20: 5.4.7.1 a)"
    },
    {
      "nr": "5.5.a",
      "text": "a) Verringerung der freien Abstände zwischen dem äußeren Rand des Fahrkorbdachs und angrenzenden Wänden und bis zur vollen Höhe auf 0,30 m oder"
    },
    {
      "nr": "5.5.b",
      "text": "b) Einbau einer Umwehrung auf dem Fahrkorbdach (höhe 0,7m bei Abstand 0,5m, höhe 1,1m bei Abstand über 0,5m) nach EN81-20: 5.4.7.4. oder"
    },
    {
      "nr": "5.5.c",
      "text": "c) Einbau einer Umwehrung auf dem Fahrkorbdach nach EN81-21: 5.6"
    },
    {
      "nr": "5.6.a",
      "text": "Versehen einer Fahrkorbbelüftung nach EN81-20: 5.4.9"
    },
    {
      "nr": "5.7.a",
      "text": "Einbau einer Beleuchtung nach EN81-20: 5.4.10.1; 5.4.10.2; 5.4.10.3"
    },
    {
      "nr": "5.8.a",
      "text": "Einbau einer Notbeleuchtung im Fahrkorb nach  EN81-20: 5.4.10.4"
    },
    {
      "nr": "5.9.a",
      "text": "Einbau einer Notbeleuchtung auf dem Fahrkorb nach EN81-20: 5.4.10.4"
    },
    {
      "nr": "5.10.a",
      "text": "Einbau einer Kontrolle der Beladung nach EN81-20: 5.12.1.2"
    },
    {
      "nr": "5.11.a",
      "text": "Einbau einer Fernnotrufeinrichtung nach EN81-28"
    },
    {
      "nr": "6.1.a",
      "text": "Einbau eines Schutzes gegen Verletzungen an Treibscheiben, Seilrollen oder Kettenrädern nach EN81-20: 5.5.7"
    },
    {
      "nr": "6.2.a",
      "text": "Einbau eines Schutzes gegen das Herausspringen von Seilen / Ketten und Treibscheibe oder Kettenrädern nach EN81-20: 5.5.7"
    },
    {
      "nr": "6.3.a",
      "text": "Einbau eines Schutzes gegen das Eindringen von Fremdkörpern zwischen Seilen / Ketten aus der Treibscheibe, Rollen oder Kettenrädern nach EN81-20: 5.5.7"
    },
    {
      "nr": "6.4.a",
      "text": "a) Prüfen, ob alle bestehenden Komponenten für den Schutz gegen freien Fall und Abwärtsbewegungen mit überhöhter Geschwindigkeit angemessen und kompatibel sind oder, wenn nicht,"
    },
    {
      "nr": "6.4.b",
      "text": "b) Einbau eines Schutzes gegen freien Fall und Abwärtsbewegungen mit überhöhter Geschwindigkeit nach EN81-20: Tabelle 11 oder Tabelle 12 (Bremsfang; Sperfang v<0,63m/s bzw. <1,0m/s mit Dämpfung; Leitungsbruchventil; Drossel)"
    },
    {
      "nr": "6.5.a",
      "text": "Einbau eines geeigneten Schutzes gegen unkontrollierte Aufwärtsbewegungen nach EN81-20: 5.6.6"
    },
    {
      "nr": "6.6.a",
      "text": "Einbau von Schutzmaßnahmen gegen unbeabsichtigte Bewegungen nach EN 8-20: 5.6.7"
    },
    {
      "nr": "6.7.a",
      "text": "Vorsehen von Schutzmaßnahmen gegen Absinken nach EN81-20: Tabelle 12"
    },
    {
      "nr": "6.8.a",
      "text": "Einbau einer elektrischen Sicherheitseinrichtung nach EN81-20: 5.6.2.2.1.6 c)"
    },
    {
      "nr": "6.9.a",
      "text": "Einbau einer Sicherheitseinrichtung gegen Schlaffseil -/- kette nach EN81-20: 5.5.5.3"
    },
    {
      "nr": "7.1.a",
      "text": "a) Einbau von festen Führungsschienen nach EN81-20: 5.7 oder"
    },
    {
      "nr": "7.1.b",
      "text": "b) Einbau von Führungen nach EN81-21: 5.4.3"
    },
    {
      "nr": "7.2.a",
      "text": "Einbau von Puffern nach EN81-20: 5.8"
    },
    {
      "nr": "7.3.a",
      "text": "Einbau eines Sicherheitsschalters nach EN81-20: 5.8"
    },
    {
      "nr": "8.1.a",
      "text": "Einbau von Bremsen nach EN-20: 5.9.2.2.2"
    },
    {
      "nr": "8.2.a",
      "text": "a) Einbau eines Notbetriebssystems für Seilaufzüge und Kettenaufzüge nach  EN81-20: 5.9.2.3 oder"
    },
    {
      "nr": "8.2.b",
      "text": "b) Hydraulikaufzüge nach EN81-20: 5.9.3.9 und"
    },
    {
      "nr": "8.2.c",
      "text": "c) Bereitstellen von Betriebsanweisungen nach EN81-20: 7.2.2"
    },
    {
      "nr": "8.3.a",
      "text": "Einbau von unabhängigen Schalteinrichtungen nach EN81-20: 5.9.2.7 oder 5.9.3.10"
    },
    {
      "nr": "8.4.a",
      "text": "Einbau einer Laufzeitüberwachung nach EN81-20: 5.9.2 bzw. 5.9.3"
    },
    {
      "nr": "8.5.a",
      "text": "Einbau eines Absperrventils nach EN81-20: 5.9.3.5.1"
    },
    {
      "nr": "8.6.a",
      "text": "Einbau einer Kolbenbabsinkvorrichtung unter Berücksichtigung von EN81-20: 5.9.3.9.1.5"
    },
    {
      "nr": "9.1.a",
      "text": "Einbau eines Schutzes gegen direktes Berühren nach  EN81-20: 5.10.1.2.2"
    },
    {
      "nr": "9.2.a",
      "text": "Anbringen von Kennzeichnungen an elektrischen Einrichtungen nach EN81-20: 5.10.6.3.5"
    },
    {
      "nr": "9.3.a",
      "text": "Einbau eines Schutzes gegen Überhitzung des Aufzugsmaschinenmotors nach EN81-20: 5.10.4"
    },
    {
      "nr": "9.4.a",
      "text": "Einbau abschließbarer Hauptschalter nach EN81-20: 5.10.5"
    },
    {
      "nr": "9.5.a",
      "text": "Einbau einer Einrichtung nach EN81-20: 5.12.1.11.1"
    },
    {
      "nr": "10.1.a",
      "text": "Einbau von Erdfehlerschutz (FI Schutzschalter) nach EN81-20: 5.11.1.4"
    },
    {
      "nr": "10.2.a",
      "text": "Einbau eines Phasenumkehrumkehrschutzes nach EN81-20: 5.11.1.2 j), um sicherzustellen, dass eine Phasenumkehr der Hauptstromversorgung keine gefährliche Fehlfunktion eines Aufzugs verursachen kann"
    },
    {
      "nr": "10.3.a",
      "text": "Einbau von Einrichtungen zur Nachregulierungs- und Anhaltegenauigkeit nach EN81-20: 5.12.1.1.4"
    },
    {
      "nr": "10.4.a",
      "text": "a) Einbau einer Inspektionssteuerung nach EN81-20: 5.4.9 a), und"
    },
    {
      "nr": "10.4.b",
      "text": "b) Einbau einer Bremseinrichtung nach EN81-20: 5.4.8 b)"
    },
    {
      "nr": "10.5.a",
      "text": "Einbau einer Inspektionssteuerung nach EN81-20: 5.2.1.5.1 b) in der Schachtgrube"
    },
    {
      "nr": "11.1.a",
      "text": "Bereitstellung zutreffender Hinweise, Kennzeichnungen und Betriebsanleitungen nach EN81-20: 5.2.4; 5.4.2.3.2; 5.4.2.3.3; 5.12.1.5.2.4; 5.12.1.11.1; 7.2"
    },
    {
      "nr": "11.2.a",
      "text": "Herstellen entsprechender Kennzeichnung"
    },
    {
      "nr": "11.3.a",
      "text": "Einbau eines Schalters nach EN81-20: 5.2.1.4.1"
    },
    {
      "nr": "11.4.a",
      "text": "Einbau einer Steckdose nach EN81-20: 5.10.7.2"
    },
    {
      "nr": "11.5.a",
      "text": "Erstellung und Anbringung der Kennzeichnung gem. EN81-20: 5.2.6.2.1"
    },
    {
      "nr": "11.6.a",
      "text": "Herstellen der Aufkantung gem. EN81-20: 5.2.6.7.2"
    },
    {
      "nr": "11.7.a",
      "text": "Erstellen eines Rahmens zur sicheren Lagerung der Einlagen gem. EN81-20: 5.4.11.2"
    },
    {
      "nr": "12.1.a",
      "text": "Dokumentation über den Schutz gegen Cyberangriffe an sicherheitsrelevante MSR-Einrichtungen (Mess-, Steuer- und Regeleinrichtungen) an Aufzügen."
    }
  ],
  "scProtections": [
    {
      "nr": "B0.a",
      "text": "EN 81-70: Fahrkorbabmessungen, Türbreite. Siehe Tabelle 1: Aufzug für Rollstühle mit Begleitperson (DIN EN 12183) oder elektrischer Rollstuhl der Klassen A, B, C  (DIN EN 12184)"
    },
    {
      "nr": "B1.a",
      "text": "Maßnahmen nach EN 81-70: 5.4.3, Anhang E  durchführen (akustische Signale, optische Anzeigen ≥ 40mm, kontrastreich)"
    },
    {
      "nr": "B2.a",
      "text": "Maßnahmen nach EN 81-70: 5.4.3, Anhang E durchführen (optische Anzeigen ≥ 40mm, kontrastreich)"
    },
    {
      "nr": "B3.a",
      "text": "Maßnahmen nach EN 81-70: 5.4.4.3 Absatz c durchführen (z.B. in öffentlichen Gebäuden)"
    },
    {
      "nr": "B4.a",
      "text": "Erreichbarkeit von Schlüsselschaltern u.ä. sicherstellen. Sicherstellen, dass ein Einschluss ausgeschlossen ist."
    },
    {
      "nr": "C0.a",
      "text": "Einhaltung der EN 81-70: Tabelle 2, ggf. Berücksichtigung Anhang F und G."
    },
    {
      "nr": "C1.a",
      "text": "Einhaltung der EN 81-70: Tabelle 2, ggf. Berücksichtigung Anhang G."
    },
    {
      "nr": "D0.a",
      "text": "Maßnahmen bei z.B. Glasschacht, Außenanlage treffen."
    },
    {
      "nr": "E0.a",
      "text": "EN 81-70: 5.2.2 Hindernisfreier Zugang zu Haltestellen sicherstellen"
    },
    {
      "nr": "F0.a",
      "text": "Arbeitsstättenschutzgesetz (keine Hindernisse im Zugang zu Betriebsräumen)"
    },
    {
      "nr": "F1.a",
      "text": "EN 81-20: 5.2.5.7 und 5.2.5.8"
    },
    {
      "nr": "F2.a",
      "text": "Arbeitsstättenschutzgesetz"
    },
    {
      "nr": "G0.a",
      "text": "Arbeitsstättenschutzgesetz (z.B. Gefahrstoffe), Überwachungskontakte usw."
    },
    {
      "nr": "G1.a",
      "text": "Arbeitsstättenschutzgesetz"
    },
    {
      "nr": "G2.a",
      "text": "Tragfähigkeitsangabe im Beladebereich bei Lastenaufzügen vorhanden? Piktogramme?"
    },
    {
      "nr": "G3.a",
      "text": "Kennzeichnung gem. Gefahrstoffverordnung anbringen"
    },
    {
      "nr": "G4.a",
      "text": "Maßnahmen nach EN 81-70: 5.4.3.4 durchführen"
    },
    {
      "nr": "G5.a",
      "text": "Maßnahmen nach BetrSichV durchführen in Verbindung mit EN 81-28"
    },
    {
      "nr": "G6.a",
      "text": ""
    }
  ]
}`;
    }
  },
}
</script>

<style scoped>

.width200 {
  min-width: 200px;
  max-width: 200px;
  width: 200px;
}

.horizontal {
  display: flex;
  flex-direction: row;
  background-color: transparent;
}

.error-border {
  border: 1px solid #ff4081;
}
.border {
  border: 1px solid lightgrey;
}

</style>